// useCanvasSelection — selection/connect/kontextmeny-glue för
// WorkflowCanvas. Extraherad ur WorkflowCanvas.tsx (#2 i
// PRE_RELEASE_REVIEW_100, slice 3).
//
// Äger:
//   • connect-from-state (port-klick A → port-klick B = connector)
//   • connector-kontextmenyns state
//   • contextmenu-world-koord-capture för "Add block"-placering
//   • block/group-select-routing (group-mode → toggle selection)
//   • canvas-klick (tomt = avmarkera + avbryt pågående connect)
//   • minimap-navigering (smooth scroll till world-punkt)

import { useState, useRef, useCallback, type RefObject } from 'react';
import type { BlockType, PortSide } from '@/types/workflow';
import type { ConnectorContextMenuState } from '@/components/editor/ConnectorContextMenu';
import { dispatchOnboardingEvent } from '@/components/onboarding/uiAdapter';

interface UseCanvasSelectionArgs {
  canvasRef: RefObject<HTMLDivElement>;
  viewOnly: boolean;
  groupMode: boolean;
  contentScale: number;
  getOrigin: () => { x: number; y: number };
  onSelectBlock: (id: string) => void;
  onToggleGroupSelection: (id: string) => void;
  onSelectGroup: (id: string | null) => void;
  onConnect: (fromId: string, toId: string, fromPort?: PortSide, toPort?: PortSide) => void;
  onCanvasClick: () => void;
  onCreateBlock: (type: BlockType, x: number, y: number, nameOverride?: string) => void;
}

export function useCanvasSelection({
  canvasRef,
  viewOnly,
  groupMode,
  contentScale,
  getOrigin,
  onSelectBlock,
  onToggleGroupSelection,
  onSelectGroup,
  onConnect,
  onCanvasClick,
  onCreateBlock,
}: UseCanvasSelectionArgs) {
  const [connectFromId, setConnectFromId] = useState<string | null>(null);
  const [connectFromPort, setConnectFromPort] = useState<PortSide | null>(null);
  // C6: right-click-menu-state för connector-linjer.
  const [connectorMenu, setConnectorMenu] = useState<ConnectorContextMenuState | null>(null);
  /**
   * Last contextmenu world-coords — captured from the canvas-inner wrapper so
   * "Add block" can place the new block at the exact spot the user clicked.
   */
  const canvasContextPosRef = useRef<{ x: number; y: number } | null>(null);

  /** Capture world-coords on contextmenu so "Add block" can place correctly. */
  const handleCanvasContextMenuCapture = useCallback((e: React.MouseEvent) => {
    if (!canvasRef.current) return;
    const canvasInner = canvasRef.current.querySelector('.canvas-inner') as HTMLElement | null;
    if (!canvasInner) return;
    const innerRect = canvasInner.getBoundingClientRect();
    const origin = getOrigin();
    canvasContextPosRef.current = {
      x: (e.clientX - innerRect.left) / contentScale - origin.x,
      y: (e.clientY - innerRect.top) / contentScale - origin.y,
    };
  }, [canvasRef, contentScale, getOrigin]);

  const handleAddBlockFromMenu = useCallback((type: BlockType) => {
    const pos = canvasContextPosRef.current;
    if (!pos) return;
    onCreateBlock(type, pos.x, pos.y);
  }, [onCreateBlock]);

  const handleBlockSelect = useCallback((blockId: string) => {
    if (groupMode) onToggleGroupSelection(blockId);
    else onSelectBlock(blockId);
  }, [groupMode, onToggleGroupSelection, onSelectBlock]);

  const handlePortClick = useCallback((blockId: string, port: PortSide) => {
    if (viewOnly) return;
    if (!connectFromId) { setConnectFromId(blockId); setConnectFromPort(port); }
    else {
      if (connectFromId !== blockId) {
        onConnect(connectFromId, blockId, connectFromPort ?? undefined, port);
        dispatchOnboardingEvent({
          name: 'connector:created',
          data: { fromId: connectFromId, toId: blockId },
        });
      }
      setConnectFromId(null); setConnectFromPort(null);
    }
  }, [viewOnly, connectFromId, connectFromPort, onConnect]);

  const handleCanvasClick = useCallback((e: React.MouseEvent) => {
    if (e.target === canvasRef.current?.querySelector('.canvas-inner')) {
      onCanvasClick();
      setConnectFromId(null); setConnectFromPort(null);
      onSelectGroup(null);
    }
  }, [canvasRef, onCanvasClick, onSelectGroup]);

  const handleMinimapNavigate = useCallback((x: number, y: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.scrollTo({
      left: Math.max(0, x - canvas.clientWidth / 2),
      top: Math.max(0, y - canvas.clientHeight / 2),
      behavior: 'smooth',
    });
  }, [canvasRef]);

  const handleGroupClick = useCallback((groupId: string) => onSelectGroup(groupId), [onSelectGroup]);

  return {
    connectFromId,
    connectorMenu,
    setConnectorMenu,
    handleCanvasContextMenuCapture,
    handleAddBlockFromMenu,
    handleBlockSelect,
    handlePortClick,
    handleCanvasClick,
    handleMinimapNavigate,
    handleGroupClick,
  };
}
